/**
 * The "chrome" props of `AppLayoutProps` that `AppLayout` itself never renders.
 * `_app.tsx` reads them off the `<AppLayout>` element's props instead, to drive the persistent
 * `AppHeader`/`NavigationBar`, which stay mounted across route changes instead of fading out/in
 * with the page content — see the class JSDoc on `AppLayoutProps` in `AppLayout.tsx`.
 */
interface AppLayoutChromeProps {
    /** The key of the currently active navigation tab. */
    activeTab: string;
    /** Whether to show the back arrow in the header. */
    isBackVisible?: boolean;
    /** Callback when the header back button is pressed. Defaults to `router.back()`. */
    onBack?(): void;
    /** Whether `router.back()` should be used instead of path-segment slicing. */
    realBackLink?: boolean;
}

/**
 * Custom hook for the `AppLayout` component.
 * `AppLayout`'s props type declares `activeTab`, `isBackVisible`, `onBack`, and `realBackLink`
 * only so `_app.tsx` can read them off the `<AppLayout>` element — this hook exists so those
 * props still count as consumed within the component itself (per `@nfq/component-single-hook`,
 * a component's body may only contain a single hook call, guard clauses, and a return statement).
 *
 * @param chromeProps The chrome-only props extracted via rest destructuring in `AppLayout`.
 */
export const useAppLayoutChrome = (chromeProps: AppLayoutChromeProps): void => {
    void chromeProps;
};